/**
 * Leaderboard client — submit finished runs and fetch global rankings.
 *
 * • All requests are same-origin (`/api/...`), served by worker.ts in
 *   production. In local dev / preview (no worker), fetches fail fast and
 *   the UI degrades gracefully.
 * • Local run history is kept in localStorage so players always see their
 *   own bests even offline.
 */

import type { Difficulty, GameState } from './types';

export interface ScoreRow {
  c: string;
  d: Difficulty;
  day: number;
  rev: number;
  units: number;
  reason: 'bankrupt' | 'acquired';
  val?: number;
  t: number;
  s?: number; // run seed — server echoes it so the client can mark "you"
}

interface RunPayload {
  company: string;
  d: Difficulty;
  seed: number;
  day: number;
  reason: 'bankrupt' | 'acquired';
  rev: number;
  units: number;
  returns: number;
  peakCash: number;
  quartersProfitable: number;
  val?: number;
}

const RUNS_KEY = 'met:runs';

export interface LocalRun extends RunPayload {
  submittedAt: number;
  rank: number | null;
}

// ---------------------------------------------------------------------------
// Submission
// ---------------------------------------------------------------------------

export function buildPayload(s: GameState, reason: 'bankrupt' | 'acquired', valuation?: number): RunPayload {
  return {
    company: s.player.companyName,
    d: s.meta.difficulty,
    seed: s.meta.seed,
    day: s.time.day,
    reason,
    rev: Math.round(s.stats.totalRevenue),
    units: Math.round(s.stats.totalUnits),
    returns: Math.round(s.stats.totalReturns),
    peakCash: Math.round(s.stats.peakCash),
    quartersProfitable: s.stats.quartersProfitable,
    val: valuation,
  };
}

export async function submitRun(payload: RunPayload): Promise<{ ok: boolean; rank: number | null; error?: string }> {
  try {
    const res = await fetch('/api/scores', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = (await res.json().catch(() => null)) as { ok?: boolean; rank?: number | null; error?: string } | null;
    if (!res.ok || !data?.ok) {
      return { ok: false, rank: null, error: data?.error ?? `HTTP ${res.status}` };
    }
    return { ok: true, rank: data.rank ?? null };
  } catch {
    return { ok: false, rank: null, error: 'offline' };
  }
}

export async function fetchTopScores(): Promise<{ ok: boolean; rows: ScoreRow[] }> {
  try {
    const res = await fetch('/api/scores/top');
    if (!res.ok) return { ok: false, rows: [] };
    const data = (await res.json()) as { ok?: boolean; rows?: ScoreRow[] };
    return { ok: Boolean(data.ok), rows: Array.isArray(data.rows) ? data.rows : [] };
  } catch {
    return { ok: false, rows: [] };
  }
}

// ---------------------------------------------------------------------------
// Local run history
// ---------------------------------------------------------------------------

export function recordLocalRun(payload: RunPayload, rank: number | null): void {
  try {
    const run: LocalRun = { ...payload, submittedAt: Date.now(), rank };
    const raw = localStorage.getItem(RUNS_KEY);
    const runs = raw ? (JSON.parse(raw) as LocalRun[]) : [];
    runs.push(run);
    // Keep the 20 most recent.
    localStorage.setItem(RUNS_KEY, JSON.stringify(runs.slice(-20)));
  } catch {
    /* storage unavailable — non-fatal */
  }
}

export function getLocalRuns(): LocalRun[] {
  try {
    const raw = localStorage.getItem(RUNS_KEY);
    return raw ? (JSON.parse(raw) as LocalRun[]) : [];
  } catch {
    return [];
  }
}

/** Seeds of this player's finished runs — used to highlight their rows. */
export function getLocalRunSeeds(): Set<number> {
  return new Set(getLocalRuns().map((r) => r.seed));
}
